import { Time } from '@eagle/common';
import { DtcFaultDescriptionForThingResponse, DtcFaultDescriptionResponse, DtcSourceDescriptionResponse } from '@eagle/core-data-types';
import { DtcMatchTypes, DtcTypes, FaultUpdate, GenericLampState, MalfunctionIndicatorState } from '@eagle/data-function-types';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { useAuthenticated } from '../../auth';

interface FaultDescriptionQueryParams {
  eventData: FaultUpdate;
  thingId: string;
}

interface NewFaultUpdateFields {
  dtcBrandVariantId: number;
  dtcSourceId: number;
  faultCode?: string | null;
  severity?: string | null;
  dtcBrandCode: string | null;
  dtcMatchType?: DtcMatchTypes;
}

export const isNewEvent = (eventData: FaultUpdate): eventData is FaultUpdate & NewFaultUpdateFields => { return 'dtcMatchType' in eventData; };

export const useDtcFaultDescriptionQuery = ({ eventData, thingId }: FaultDescriptionQueryParams, { enabled = true }: { enabled?: boolean } = {}): UseQueryResult<DtcFaultDescriptionResponse | DtcFaultDescriptionForThingResponse> => {
  const { restClient } = useAuthenticated();
  const { i18n } = useTranslation();
  const i18nLang = i18n.resolvedLanguage?.split('-') || ['en'];
  const language = i18nLang[0].toLowerCase();

  return useQuery({
    queryKey: ['dtc-fault-description', { eventData, thingId, language }],
    queryFn: async () => {
      try {
        const { failureModeIdentifier } = eventData;

        if (failureModeIdentifier === undefined) {
          throw new Error('failureModeIdentifier should not be undefined.');
        }

        const { amberWarning, code, malfunctionIndicator, redStop, sourceAddress, type } = eventData;

        if (isNewEvent(eventData) && (eventData.dtcMatchType === DtcMatchTypes.EXACT || eventData.dtcMatchType === DtcMatchTypes.J1939)) {
          try {
            const result = await restClient.dtc.getDtcFaultDescriptionForBrandVariant(eventData.dtcBrandVariantId, eventData.dtcBrandCode ?? 'null', language,
              {
                amberWarning: amberWarning as GenericLampState,
                dtcType: type as DtcTypes,
                malfunctionIndicator: malfunctionIndicator as MalfunctionIndicatorState,
                fmi: failureModeIdentifier,
                redStop: redStop as GenericLampState,
              });
            return result;
          }
          catch (error) {
            if (!isAxiosError(error) || error.response?.status !== 404) throw error;
          }
        }

        if (sourceAddress === undefined) {
          throw new Error('source should not be undefined.');
        }

        return await restClient.dtc.getDtcFaultDescriptionForThing({
          amberWarning: amberWarning as GenericLampState,
          dtcCode: code,
          dtcType: type as DtcTypes,
          malfunctionIndicator: malfunctionIndicator as MalfunctionIndicatorState,
          fmi: failureModeIdentifier,
          thingId,
          language,
          redStop: redStop as GenericLampState,
          source: sourceAddress,
        });
      } catch (err) {
        return null;
      }
    },
    staleTime: Time.hours(1),
    gcTime: Time.hours(24),
    retry: false,
    enabled,
  });
};

export const useDtcSourceDescriptionQuery = ({ eventData, thingId }: FaultDescriptionQueryParams, { enabled = true }: { enabled?: boolean } = {}): UseQueryResult<DtcSourceDescriptionResponse> => {
  const { restClient } = useAuthenticated();
  const { i18n } = useTranslation();
  const i18nLang = i18n.resolvedLanguage?.split('-') || ['en'];
  const language = i18nLang[0].toLowerCase();

  return useQuery({
    queryKey: ['dtc-source-description', { eventData, thingId, language }],
    queryFn: async () => {
      try {
        if (isNewEvent(eventData) && (eventData.dtcMatchType === DtcMatchTypes.EXACT || eventData.dtcMatchType === DtcMatchTypes.J1939)) {
          return await restClient.dtc.getDtcSourceDescriptionForSource(eventData.dtcSourceId, eventData.dtcBrandCode ?? 'null', language, { dtcType: eventData.type as DtcTypes });
        }

        const { code, failureModeIdentifier, sourceAddress } = eventData;

        if (failureModeIdentifier === undefined) {
          throw new Error('failureModeIdentifier should not be undefined.');
        }

        if (sourceAddress === undefined) {
          throw new Error('source should not be undefined.');
        }

        return await restClient.dtc.getDtcSourceDescriptionForThing({
          dtcCode: code,
          dtcType: eventData.type as DtcTypes,
          fmi: failureModeIdentifier,
          thingId,
          language,
          source: sourceAddress,
        });
      } catch (err) {
        return null;
      }
    },
    staleTime: Time.hours(1),
    gcTime: Time.hours(24),
    retry: false,
    enabled,
  });
};
